import React, { useEffect, useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  Avatar,
  Paper,
  Divider,
  CircularProgress,
  Stack,
  IconButton,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducers';
import { db, firestore } from '../../config/firebase';
import { ChatRoom, ChatUser } from '../../types/chat';
import TopNavBar from '../../Components/TopNavigation/TopNavBar';
import CreateChatButton from '../../Components/Chat/CreateChatButton';
import { formatDistanceToNow } from 'date-fns';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { getUsernameById } from '../../services/users';

const ChatList: React.FC = () => {
  const navigate = useNavigate();
  const [chats, setChats] = useState<ChatRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [userNames, setUserNames] = useState<{[key: string]: string}>({});
  
  // Get current user from Redux
  const currentUser = useSelector((state: RootState) => state.user?.user);
  const currentUserId = currentUser?._id || currentUser?.id;

  console.log('🔍 ChatList Component Debug Info:', {
    currentUserId,
    currentUser,
    chatsCount: chats.length,
    loading
  });

  useEffect(() => {
    if (!currentUserId) {
      console.log('❌ No current user, skipping chat fetch');
      return;
    }


    // Listen to chats where current user is a participant
    const chatsQuery = query(
      collection(db, 'Chats'),
      where('participants', 'array-contains', currentUserId)
    );
    
    
    const unsubscribe = onSnapshot(chatsQuery, (snapshot: any) => {

        const chatList = snapshot.docs.map((doc: any) => {
          const data = doc.data();
          return {
            id: doc.id,
            participants: data.participants || [],
            lastMessage: data.lastMessage || '',
            lastMessageAt: data.lastMessageAt,
            createdAt: data.createdAt,
          };
        });
        
        setChats(chatList);
        setLoading(false);
      });

    console.log('🔌 ChatList Firestore listener set up, returning unsubscribe function');
    return unsubscribe;
  }, [currentUserId]);

  // Fetch user names when chats change
  useEffect(() => {
    if (!currentUserId || chats.length === 0) return;

    const fetchMissingUserNames = async () => {
      const missingIds = new Set<string>();
      for (const chat of chats) {
        const otherUserId = chat.participants.find((participant: any) => participant !== currentUserId);
        if (otherUserId && !userNames[otherUserId]) {
          missingIds.add(otherUserId);
        }
      }

      if (missingIds.size === 0) return;

      try {
        const entries = await Promise.all(
          Array.from(missingIds).map(async (uid) => {
            const name = await getUsernameById(uid);
            return [uid, name] as const;
          })
        );

        const newUserNames: {[key: string]: string} = {};
        for (const [uid, name] of entries) {
          if (name) newUserNames[uid] = name;
        }
        if (Object.keys(newUserNames).length > 0) {
          setUserNames(prev => ({ ...prev, ...newUserNames }));
        }
      } catch (error) {
        console.error('Error fetching user names:', error);
      }
    };

    fetchMissingUserNames();
  }, [chats, currentUserId]);

  const handleChatPress = (chatId: string,otherUserName: any) => {
    navigate(`/chat/${chatId}`,{
      state: {
        chatId,
        otherUserName: otherUserName,
        source: 'chatList',
      }
    });
  };

  const formatTime = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return formatDistanceToNow(date, { addSuffix: true });
  };

  console.log('🎨 Rendering ChatList component:', {
    chatsCount: chats.length,
    loading,
    hasCurrentUser: !!currentUserId
  });

  if (!currentUserId) {
    console.log('❌ No current user, showing login message');
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography>Please log in to access chats</Typography>
      </Box>
    );
  }

  if (loading) {
    console.log('⏳ ChatList loading, showing spinner');
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }


  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>

      {/* Create Chat Button */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton 
          onClick={() => window.history.back()}
          sx={{ color: 'black' }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" fontWeight="bold" sx={{ flex: 1 }}>
          Messages
        </Typography>
        {/* <CreateChatButton variant="outlined" fullWidth /> */}
      </Box>

      {/* Chat List */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {chats.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No conversations yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Start chatting with other users to see your conversations here.
            </Typography>
          </Box>
        ) : (
          <List>
            {chats.map((chat, index) => { 
              const otherUser = chat.participants.find((participant: any) => participant !== currentUserId);
              const otherUserName = otherUser && userNames[otherUser] ||  'Loading...';
              
              return(
              <React.Fragment key={chat.id}>
                <ListItem
                  component="div"
                  onClick={() =>{
                     handleChatPress(chat.id, otherUser)}}
                  sx={{
                    py: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        backgroundColor: 'primary.main',
                      }}
                    >
                      {chat.participants.length > 0 ? 
                        chat.participants[0].charAt(0).toUpperCase() : 
                        'U'
                      }
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1" fontWeight="medium">
                        {otherUserName}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '200px',
                          }}
                        >
                          {chat.lastMessage || 'No messages yet'}
                        </Typography>
                        {chat.lastMessageAt && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.5 }}
                          >
                            {formatTime(chat.lastMessageAt)}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                {index < chats.length - 1 && <Divider />}
              </React.Fragment>
            )})}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default ChatList;